/**
 * Unsubscribe API service - public endpoint, no auth required.
 * Calls the public BFF POST /api/insightpro/unsubscribe with { email, reason?, list_emails? }.
 * The gateway rewrites that path to /flight/v1/unsubscribe.
 */

const PUBLIC_UNSUBSCRIBE_PATH = '/api/insightpro/unsubscribe';

const BFF_BY_ENV: Record<string, string> = {
  production: 'https://web.latamcargo.com',
  intg: 'https://web.intg.latamcargo.com',
};

function publicBffOrigin(): string {
  const configured = import.meta.env.VITE_PUBLIC_BFF_BASE_URL;
  if (configured) return configured.replace(/\/$/, '');
  const env = import.meta.env.VITE_APP_ENV || 'development';
  return BFF_BY_ENV[env] ?? 'https://web.dev.latamcargo.com';
}

/** BFF error response shape per OpenAPI (400/503) */
interface UnsubscribeErrorResponse {
  error?: string;
  message?: string;
}

const REQUEST_TIMEOUT_MS = 10000;

function getErrorMessage(err: unknown, fallbackStatus: number): string {
  if (!err || typeof err !== 'object') return `Unsubscribe failed: ${fallbackStatus}`;
  const e = err as UnsubscribeErrorResponse;
  const msg = e.message ?? e.error;
  return typeof msg === 'string' && msg.length > 0 ? msg : `Unsubscribe failed: ${fallbackStatus}`;
}

export const unsubscribe = async (
  email: string,
  reason?: string,
  listEmails?: string[]
): Promise<{ status: string }> => {
  const url = `${publicBffOrigin()}${PUBLIC_UNSUBSCRIBE_PATH}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  const body: Record<string, unknown> = { email };
  if (reason && reason.trim()) body.reason = reason.trim();
  if (listEmails && listEmails.length > 0) body.list_emails = listEmails;

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(getErrorMessage(err, res.status));
    }
    return res.json();
  } catch (e) {
    if (e && typeof e === 'object' && 'name' in e && (e as Error).name === 'AbortError') {
      throw new Error('Unsubscribe request timed out');
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
};
